import { cloneDeep } from 'lodash';

import type { LoginSocialBarSdkData } from '@wix/thunderbolt-components';
import {
  withValidation,
  composeSDKFactories,
  resolveAndFetchSvg,
  createElementPropsSDKFactory,
  toJSONBase,
} from '@wix/editor-elements-corvid-utils';
import type { LoginSocialBarSdkDataCommonProps } from '@wix/thunderbolt-components-native';

import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';

import type {
  ILoginSocialBarProps,
  ILoginSocialBarOwnSDKFactory,
  ILoginSocialBarSDK,
  ILoginSocialBarSDKMenuItems,
  ILoginSocialBarSDKNavBarItems,
  LoginSocialBarMenuItemProps,
} from '../LoginSocialBar.types';

import { ELEMENT_TYPE } from './constants';
import {
  menuItemsPropertySchema,
  navBarItemsPropertySchema,
} from './validationSchemas';
import { validateNavBarItems } from './customValidators';

type CreateAccountNavBarSdkFactoryParams = {
  isMobileOnly?: boolean;
};

type ICommonItem = Partial<
  Pick<
    LoginSocialBarSdkDataCommonProps,
    'link' | 'visibleOnDesktop' | 'visibleOnMobile' | 'displayCount'
  >
>;

const getCommonOptionalItemProps = (
  { link, visibleOnDesktop, visibleOnMobile, displayCount }: ICommonItem,
  isMobileOnly: boolean,
) => ({
  ...(link !== undefined && { link }),
  ...(!isMobileOnly && { visibleOnDesktop: visibleOnDesktop ?? true }),
  visibleOnMobile: visibleOnMobile ?? true,
  ...(typeof displayCount === 'number' && { displayCount }),
});

const cloneMenuItems = (
  menuItems: ILoginSocialBarSDKMenuItems,
  isMobileOnly: boolean,
): ILoginSocialBarSDKMenuItems =>
  menuItems.map(
    ({ label, linkMetaData, items, ...optionalProps }) =>
      ({
        label,
        ...(linkMetaData !== undefined && {
          linkMetaData: cloneDeep(linkMetaData),
        }),
        items: items?.length ? cloneMenuItems(items, isMobileOnly) : [],
        ...getCommonOptionalItemProps(optionalProps, isMobileOnly),
      }) as ILoginSocialBarSDKMenuItems[0],
  );

const cloneNavBarItems = (
  navBarItems: ILoginSocialBarSDKNavBarItems,
  isMobileOnly: boolean,
) =>
  navBarItems.map(({ icon, label, linkMetaData, ...optionalProps }) => ({
    icon,
    ...(label !== undefined && { label }),
    ...(linkMetaData !== undefined && {
      linkMetaData: cloneDeep(linkMetaData),
    }),
    ...getCommonOptionalItemProps(optionalProps, isMobileOnly),
  }));

const createSdkFactory =
  ({
    isMobileOnly = false,
  }: CreateAccountNavBarSdkFactoryParams): ILoginSocialBarOwnSDKFactory =>
  ({ setProps, sdkData, metaData, createSdkState, handlers }) => {
    const { isMobileView = isMobileOnly, baseSvgMediaUrl } = sdkData;
    const isItemVisiblePredicate = ({
      visibleOnDesktop,
      visibleOnMobile,
    }: ICommonItem) => (isMobileView ? visibleOnMobile : visibleOnDesktop);

    const [state, setState] = createSdkState<{
      menuItems: ILoginSocialBarSDKMenuItems;
      navBarItems: ILoginSocialBarSDKNavBarItems;
    }>({
      menuItems: sdkData.menuItems,
      navBarItems: sdkData.navBarItems,
    });

    return {
      get menuItems() {
        return cloneMenuItems(state.menuItems, isMobileOnly);
      },

      set menuItems(value) {
        setState({
          menuItems: value ? cloneMenuItems(value, isMobileOnly) : [],
        });

        const toMenuItemProps = (
          items: ILoginSocialBarSDKMenuItems,
        ): Array<LoginSocialBarMenuItemProps> =>
          items
            .filter(isItemVisiblePredicate)
            .map(
              ({
                label,
                link,
                linkMetaData,
                displayCount,
                items: childItems,
              }) => ({
                label,
                link: {
                  ...(link && { href: link }),
                  ...cloneDeep(linkMetaData),
                },
                ...(displayCount !== undefined && { displayCount }),
                items: childItems?.length ? toMenuItemProps(childItems) : [],
              }),
            );

        setProps({ menuItems: toMenuItemProps(state.menuItems) });
      },

      get navBarItems() {
        return cloneNavBarItems(state.navBarItems, isMobileOnly);
      },

      set navBarItems(value) {
        setState({
          navBarItems: value ? cloneNavBarItems(value, isMobileOnly) : [],
        });
        const visibleNavBarItems = state.navBarItems.filter(
          isItemVisiblePredicate,
        );

        if (!visibleNavBarItems.length) {
          setProps({ iconItems: [] });
          return;
        }

        setProps(
          Promise.all(
            visibleNavBarItems.map(({ icon }) =>
              resolveAndFetchSvg(icon, baseSvgMediaUrl, handlers.sanitizeSVG),
            ),
          ).then(icons => ({
            iconItems: visibleNavBarItems.map(
              ({ label, link, linkMetaData, displayCount }, index) => ({
                iconSvgContent: icons[index],
                label: label ?? '',
                link: {
                  ...(link ? { href: link } : {}),
                  ...cloneDeep(linkMetaData),
                },
                displayCount,
              }),
            ),
          })),
        );
      },
      get type() {
        return ELEMENT_TYPE;
      },

      toJSON() {
        return {
          ...toJSONBase(metaData),
          type: ELEMENT_TYPE,
        };
      },
    };
  };

export const createAccountNavBarSdkFactory = (
  params: CreateAccountNavBarSdkFactoryParams = {},
) =>
  withValidation(
    createSdkFactory(params),
    {
      type: ['object'],
      properties: {
        menuItems: menuItemsPropertySchema,
        navBarItems: navBarItemsPropertySchema,
      },
    },
    {
      navBarItems: [validateNavBarItems],
    },
  );

const loginSocialBarSDKFactory = createAccountNavBarSdkFactory();
const elementPropsSDKFactory = createElementPropsSDKFactory();

export const sdk = composeSDKFactories<
  ILoginSocialBarProps,
  ILoginSocialBarSDK,
  LoginSocialBarSdkData
>([elementPropsSDKFactory, loginSocialBarSDKFactory]);

export default createComponentSDKModel(sdk);
